/**
 * A component that renders action buttons for a table row with optional confirmation dialogs.
 * 
 * @component
 * @example
 * ```tsx
 * <RowActions
 *   rowData={{ id: 1, name: 'John' }}
 *   rowId={1}
 *   actions={[
 *     {
 *       id: 'edit',
 *       label: 'Edit',
 *       onClick: (data) => console.log('Edit', data),
 *       variant: 'primary'
 *     }
 *   ]}
 *   userRoleLevel={2}
 * />
 * ```
 * 
 * @param {Object} props - Component props
 * @param {any} props.rowData - The data object for the current row
 * @param {any} props.rowId - The unique identifier for the row
 * @param {RowActionConfig[]} props.actions - Array of action configurations to display
 * @param {number} [props.userRoleLevel=1] - The current user's role level for permission checks
 * @param {React.CSSProperties} [props.cellWidth] - Optional CSS styles for cell width
 * @param {boolean} [props.isFirstRow] - Whether this is the first row (affects border styling)
 * @param {boolean} [props.isRowSelected] - Whether the row is currently selected
 * 
 * @returns {JSX.Element} A row actions container with action buttons and optional confirmation dialog
 * 
 * @remarks
 * - Actions can be filtered based on visibility conditions and user role levels
 * - Supports confirmation dialogs for destructive actions via portal to document.body
 * - Handles loading states for async actions
 * - Supports custom action variants (primary, danger, etc.)
 */
// rowActions.tsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './rowActions.css';
import { RowActionConfig } from '../tableMain';
import WarningBox from '../../common/WarningBox';

interface RowActionsProps {
    rowData: any;
    rowId: any;
    actions: RowActionConfig[];
    userRoleLevel?: number;
    cellWidth?: React.CSSProperties;
    isFirstRow?: boolean;
    isRowSelected?: boolean;
}

const RowActions: React.FC<RowActionsProps> = ({
    rowData,
    rowId,
    actions,
    userRoleLevel = 1,
    cellWidth,
    isFirstRow,
    isRowSelected,
}) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState<RowActionConfig | null>(null);
    const [isLoading, setIsLoading] = useState<Set<string>>(new Set());

    const handleActionClick = async (action: RowActionConfig) => {
        // Check if action requires confirmation
        if (action.requiresConfirmation && action.confirmationConfig) {
            setPendingAction(action);
            setShowConfirmation(true);
            return;
        }

        // Execute action directly
        await executeAction(action);
    };

    const executeAction = async (action: RowActionConfig, reason?: string) => {
        setIsLoading(prev => new Set(prev).add(action.id));

        try {
            await action.onClick(rowData, rowId, reason);
        } catch (error) {
            console.error(`Error executing action ${action.id}:`, error);
        } finally {
            setIsLoading(prev => {
                const newSet = new Set(prev);
                newSet.delete(action.id);
                return newSet;
            });
        }
    };
    //handlers for confirming action - not tied to button directly, only to allow for confirmation dialog
    const handleConfirm = async (reason?: string) => {
        if (pendingAction) {
            await executeAction(pendingAction, reason);
            setShowConfirmation(false);
            setPendingAction(null);
        }
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        setPendingAction(null);
    };

    // Filter visible actions
    const visibleActions = actions.filter(action => {
        // Check visibility condition
        if (action.isVisible && !action.isVisible(rowData)) {
            return false;
        }

        // Check role level
        if (action.minRoleLevel && userRoleLevel < action.minRoleLevel) {
            return false;
        }

        return true;
    });

    if (visibleActions.length === 0) {
        return (
            <div
                className="rowActions-container"
                style={{
                    ...cellWidth,
                    ...(isFirstRow && { borderTop: '1.5px solid var(--color-grey-border-light)' }),
                }}
            />
        );
    }

    return (
        <>
            <div
                className={`rowActions-container ${isRowSelected ? 'selected' : ''}`}
                style={{
                    ...cellWidth,
                    ...(isFirstRow && { borderTop: '1.5px solid var(--color-grey-border-light)' }),
                }}
            >
                {visibleActions.map(action => {
                    const isDisabled = action.isDisabled?.(rowData) || isLoading.has(action.id);
                    const buttonClass = `rowActions-button rowActions-button--${action.variant || 'primary'} ${
                        isDisabled ? 'rowActions-button--disabled' : ''
                    }`;

                    return (
                        <button
                            key={action.id}
                            className={buttonClass}
                            onClick={() => handleActionClick(action)}
                            disabled={isDisabled}
                        >
                            {action.icon && <span className="rowActions-icon">{action.icon}</span>}
                            <span className="rowActions-label">{action.label}</span>
                        </button>
                    );
                })}
            </div>
            {/* table may be rendered in too small of a container to show the confirmation dialog properly, so use a portal to dom body */}
            {showConfirmation && pendingAction?.confirmationConfig && createPortal(
                <WarningBox
                    isOpen={showConfirmation}
                    onClose={handleCancel}
                    onConfirm={handleConfirm}
                    title={pendingAction.confirmationConfig.title}
                    message={
                        typeof pendingAction.confirmationConfig.message === 'function'
                            ? pendingAction.confirmationConfig.message(rowData)
                            : pendingAction.confirmationConfig.message
                    }
                    button1="Cancel"
                    button2="Confirm"
                    showbutton2={true}
                    showTextInput={
                        typeof pendingAction.confirmationConfig.requiresReason === 'function'
                            ? pendingAction.confirmationConfig.requiresReason(rowData)
                            : pendingAction.confirmationConfig.requiresReason
                    }
                    textPlaceholder={
                        (typeof pendingAction.confirmationConfig.requiresReason === 'function'
                            ? pendingAction.confirmationConfig.requiresReason(rowData)
                            : pendingAction.confirmationConfig.requiresReason)
                            ? 'Type reason...'
                            : undefined
                    }
                    button2Color={pendingAction.variant === 'danger' ? 'red' : 'green'}
                />,
                document.body
            )}
        </>
    );
};

export default RowActions;
